import { useId } from "react"
import { cn } from "@/lib/utils"
import { Switch } from "@/components/ui/switch"
import type { FuriganaMode } from "@/hooks/use-furigana-preference"

const MODES: { value: FuriganaMode; label: string }[] = [
  { value: "none", label: "Hide" },
  { value: "unknown", label: "Unknown" },
  { value: "all", label: "All" },
]

interface FuriganaModeSelectorProps {
  mode: FuriganaMode
  onModeChange: (mode: FuriganaMode) => void
  className?: string
}

// Segmented control: three equal-width options, the current one raised.
export function FuriganaModeSelector({
  mode,
  onModeChange,
  className,
}: FuriganaModeSelectorProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Furigana"
      className={cn(
        "grid grid-cols-3 gap-0.5 rounded-full bg-[var(--selected,var(--muted))] p-0.5",
        className
      )}
    >
      {MODES.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={mode === value}
          onClick={() => onModeChange(value)}
          className={cn(
            "h-8 rounded-full text-[13px] transition-colors",
            mode === value
              ? "bg-[var(--surface-input,var(--background))] font-semibold text-[var(--label,var(--foreground))] shadow-sm"
              : "font-medium text-muted-foreground hover:text-[var(--label,var(--foreground))]"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

interface SettingsSwitchRowProps {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  className?: string
}

// A label filling the row with its switch flush right.
export function SettingsSwitchRow({
  label,
  checked,
  onCheckedChange,
  className,
}: SettingsSwitchRowProps) {
  const id = useId()
  return (
    <div className={cn("flex items-center gap-4", className)}>
      <label
        htmlFor={id}
        className="flex-1 cursor-pointer text-[var(--label,var(--foreground))]"
      >
        {label}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

interface FuriganaControlsProps {
  mode: FuriganaMode
  onModeChange: (mode: FuriganaMode) => void
  firstOnly: boolean
  onFirstOnlyChange: (firstOnly: boolean) => void
  /** Classes for the mode selector's wrapper and the first-only row. */
  selectorClassName?: string
  rowClassName?: string
}

// Furigana mode selector with the "First occurrence only" toggle beneath it.
// "First only" only applies when furigana is shown, so it's hidden in "Hide".
export function FuriganaControls({
  mode,
  onModeChange,
  firstOnly,
  onFirstOnlyChange,
  selectorClassName,
  rowClassName,
}: FuriganaControlsProps) {
  return (
    <>
      <div className={selectorClassName}>
        <FuriganaModeSelector mode={mode} onModeChange={onModeChange} />
      </div>
      {mode !== "none" && (
        <SettingsSwitchRow
          className={rowClassName}
          label="First occurrence only"
          checked={firstOnly}
          onCheckedChange={onFirstOnlyChange}
        />
      )}
    </>
  )
}
